import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  Grid,
  Alert,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import axios from 'axios';

/**
 * Edit School Page (SuperAdmin)
 */
const EditSchool: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    email: '', // Kept in state but hidden from UI
    phone: '', // Kept in state but hidden from UI
    address: '', // Kept in state but hidden from UI
    city: '',
    state: '',
    country: '',
    postalCode: '', // Kept in state but hidden from UI
    isActive: true,
    // SuperAdmin managed fields
    billingName: '',
    accountStartYear: new Date().getFullYear(),
    sessionStartMonth: 4, // April default
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchData();
    }
    console.log(id)
  }, [id]);

  const fetchData = async () => {
    try {
      const schoolRes = await axios.get(`/schools/${id}`);
      const school = schoolRes.data;

      setFormData({
        name: school.name || '',
        code: school.code || '',
        email: school.email || '',
        phone: school.phone || '',
        address: school.address || '',
        city: school.city || '',
        state: school.state || '',
        country: school.country || '',
        postalCode: school.postalCode || '',
        isActive: school.isActive ?? true,
        billingName: school.billingName || '',
        accountStartYear: school.accountStartYear || new Date().getFullYear(),
        sessionStartMonth: school.sessionStartMonth || 4,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError('');
  setLoading(true);

  try {
    const {
      code, // ❌ REMOVE code
      ...updatePayload
    } = formData;

    await axios.patch(`/schools/${id}`, updatePayload);

    window.dispatchEvent(new Event('schoolUpdated'));
    navigate(`/superadmin/schools`);
  } catch (err: any) {
    setError(err.response?.data?.message || err.message || 'Failed to update school');
  } finally {
    setLoading(false);
  }
};

  if (fetching) {
    return (
      <MainLayout>
        <Container maxWidth="md">
          <Typography>Loading...</Typography>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/superadmin/schools')}
            sx={{ mb: 2 }}
          >
            Back to Schools
          </Button>
          <Typography variant="h4">Edit School</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="School Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="School Code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Email Address"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  required
                  label="Phone No"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PIN"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Billing Name"
                  name="billingName"
                  value={formData.billingName}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Account Start Year"
                  name="accountStartYear"
                  value={formData.accountStartYear}
                  onChange={handleChange}
                  disabled={loading}
                  inputProps={{ min: 2000, max: 2100 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Session Start Month"
                  name="sessionStartMonth"
                  value={formData.sessionStartMonth}
                  onChange={handleChange}
                  disabled={loading}
                  inputProps={{ min: 1, max: 12 }}
                  helperText="Month number (1-12)"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/superadmin/schools')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </MainLayout>
  );
};

export default EditSchool;
